import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { Formik } from "formik";
import axios from "axios";
import {
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  Alert as MuiAlert,
  Box,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Button as MuiButton,
  TextField as MuiTextField,
  Divider,
  FormControlLabel,
  Checkbox,
  DialogActions,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import Select from "./SelectBox";
import { IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

const Card = styled(MuiCard)(spacing);
const Alert = styled(MuiAlert)(spacing);
const TextField = styled(MuiTextField)(spacing);
const Button = styled(MuiButton)(spacing);

const initialValues = {
  bankAccountId: "",
  entityId: "",
  shortDescription: "",
  extendedDescription: "",
  bankAccountTypeId: "",
  iban: "",
  bic: "",
  bankName: "",
  bankAddress: "",
  bankPostalCode: "",
  bankCity: "",
  countryId: "",
  isDefault: false,
};

const validationSchema = Yup.object().shape({
  shortDescription: Yup.string().required("Required"),
  extendedDescription: Yup.string().required("Required"),
  bankAccountTypeId: Yup.string().required("Account Type is required"),
  iban: Yup.string().required("Required"),
  bic: Yup.string().required("Required"),
  bankName: Yup.string().required("Required"),
  bankAddress: Yup.string().required("Required"),
  bankPostalCode: Yup.string().required("Required"),
  bankCity: Yup.string().required("Required"),
  countryId: Yup.string().required("Country is required"),
  isDefault: Yup.boolean(),
});

const AddBankAccount = ({ onDataRefresh, customerId, iconOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitInError, setSubmitInError] = useState(false);
  const [bankAccountTypeData, setBankAccountTypeData] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    values: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const responseBankAccountType = await axios.get("/api/v1/bankaccounttypes");
        const responseCountry = await axios.get("/api/v1/countries");

        setBankAccountTypeData(responseBankAccountType.data);
        setCountryData(responseCountry.data);
      } catch (error) {
        console.error("Error fetching data: ", error);
      }
    };

    fetchData();
  }, []);

  const validateIban = async (iban) => {
    if (!iban) return { isValid: true };

    try {
      const response = await axios.get(`/api/v1/bankaccounts/by-iban/${encodeURIComponent(iban)}`);
      const bankAccounts = response.data;

      if (!bankAccounts || !Array.isArray(bankAccounts)) {
        return { isValid: true };
      }

      // Check if IBAN exists for the same entity
      const existsForSameEntity = bankAccounts.some((account) => account.entityId?.toString() === customerId?.toString());

      if (existsForSameEntity) {
        return {
          isValid: false,
          message: "This IBAN is already registered for this customer",
        };
      }

      // Check if IBAN exists for different entity
      const existsForDifferentEntity = bankAccounts.some((account) => account.entityId?.toString() !== customerId?.toString());

      if (existsForDifferentEntity) {
        try {
          const firstDifferentAccount = bankAccounts.find((account) => account.entityId?.toString() !== customerId?.toString());

          if (firstDifferentAccount?.entityId) {
            const entityResponse = await axios.get(`/api/v1/entities/${firstDifferentAccount.entityId}`);
            const entityName = entityResponse.data?.name || "another customer";
            return {
              isValid: false,
              message: `This IBAN is already registered for ${entityName}`,
              needsConfirmation: true,
              entityName,
            };
          }
        } catch (error) {
          console.error("Error fetching entity details:", error);
          return {
            isValid: false,
            message: "This IBAN is already registered for another customer",
            needsConfirmation: true,
          };
        }
      }

      return { isValid: true };
    } catch (error) {
      console.error("IBAN validation error:", error);
      if (error.response?.status === 404) {
        return { isValid: true };
      }
      return {
        isValid: false,
        message: `Unable to validate IBAN: ${error.response?.data?.message || error.message || "Unknown error"}`,
      };
    }
  };

  const handleSubmit = async (values, { resetForm, setErrors, setStatus, setSubmitting }) => {
    setIsValidating(true);
    try {
      // Validate IBAN first
      const ibanValidation = await validateIban(values.iban);
      if (!ibanValidation.isValid) {
        if (ibanValidation.needsConfirmation) {
          setConfirmDialog({
            open: true,
            title: "Confirm IBAN Usage",
            message: `This IBAN is already registered for ${
              ibanValidation.entityName || "another customer"
            }. Are you sure you want to proceed?`,
            onConfirm: () => submitBankAccount(values, { resetForm, setErrors, setStatus, setSubmitting }),
            values,
          });
          setIsValidating(false);
          return;
        }
        setErrorMessage(ibanValidation.message);
        setSubmitInError(true);
        setSubmitting(false);
        setIsValidating(false);
        return;
      }

      await submitBankAccount(values, { resetForm, setErrors, setStatus, setSubmitting });
    } catch (error) {
      console.error("Error: ", error);
      setErrorMessage("Error: " + error.message);
      setSubmitInError(true);
      setIsValidating(false);
    }
  };

  const submitBankAccount = async (values, { resetForm, setErrors, setStatus, setSubmitting }) => {
    try {
      const newBankAccount = {
        entityId: customerId,
        shortDescription: values.shortDescription,
        extendedDescription: values.extendedDescription,
        bankAccountType: values.bankAccountTypeId,
        iban: values.iban,
        bic: values.bic,
        bankName: values.bankName,
        bankAddress: values.bankAddress,
        bankPostalCode: values.bankPostalCode,
        bankCity: values.bankCity,
        countryCode: values.countryId,
        isDefault: values.isDefault,
      };

      const response = await axios.post("/api/v1/bankaccounts", newBankAccount);
      if (response.status >= 200 && response.status < 300) {
        onDataRefresh();
        setErrorMessage("Your data has been submitted successfully!");
        setSubmitInError(false);
        setOpen(false);
      } else {
        console.error("Error adding new payment type.");
        setErrorMessage("Error adding new payment type.");
        setSubmitInError(true);
      }
    } catch (error) {
      console.error("Error: ", error);
      setErrorMessage("Error: " + error.message);
      setSubmitInError(true);
    } finally {
      setSubmitting(false);
      setIsValidating(false);
      setConfirmDialog((prev) => ({ ...prev, open: false }));
    }
  };

  const addButton = (
    <IconButton
      style={{ margin: 0, padding: 0 }}
      color="primary"
      aria-label="add bank account"
      onClick={() => setOpen(true)}
    >
      <AddIcon />
    </IconButton>
  );

  return (
    <>
      {iconOnly ? (
        addButton
      ) : (
        <Card mb={0}>
          <Box display="flex" justifyContent="flex-start" alignItems="center" p={0} m={0}>
            {addButton}
          </Box>
        </Card>
      )}
      <Dialog open={open} onClose={() => window.location.reload(false)} aria-labelledby="form-dialog-title" fullWidth maxWidth="md">
        <DialogTitle id="form-dialog-title">Add Bank Account</DialogTitle>
        <Divider />
        <DialogContent>
          <Formik
            initialValues={{ ...initialValues, entityId: customerId }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, status }) => (
              <Card mb={6}>
                <CardContent>
                  {submitInError ? (
                    <Alert severity="error" my={3}>
                      {errorMessage}
                    </Alert>
                  ) : errorMessage.length > 0 ? (
                    <Alert severity="success" my={3}>
                      Your data has been submitted successfully!
                    </Alert>
                  ) : (
                    ""
                  )}

                  {isSubmitting || isValidating ? (
                    <Box display="flex" justifyContent="center" my={20} mx={40}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <form onSubmit={handleSubmit}>
                      <Grid container spacing={6}>
                        <Grid item md={6} xs={12}>
                          <TextField
                            name="iban"
                            label="IBAN"
                            value={values.iban}
                            error={Boolean(touched.iban && errors.iban)}
                            fullWidth
                            helperText={touched.iban && errors.iban}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                        <Grid item md={2} xs={12}>
                          <TextField
                            name="bic"
                            label="BIC"
                            value={values.bic}
                            error={Boolean(touched.bic && errors.bic)}
                            fullWidth
                            helperText={touched.bic && errors.bic}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                        <Grid item md={4} xs={12}>
                          <Select
                            name="bankAccountTypeId"
                            label="Account Type"
                            options={bankAccountTypeData.map((item) => ({
                              key: item.bankAccountTypeId,
                              value: item.shortCode,
                            }))}
                            onChange={handleChange}
                            error={Boolean(touched.bankAccountTypeId && errors.bankAccountTypeId)}
                            helperText={touched.bankAccountTypeId && errors.bankAccountTypeId}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={6}>
                        <Grid item md={12} xs={12}>
                          <TextField
                            name="shortDescription"
                            label="Short Description"
                            value={values.shortDescription}
                            error={Boolean(touched.shortDescription && errors.shortDescription)}
                            fullWidth
                            helperText={touched.shortDescription && errors.shortDescription}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="shortDescription"
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={6}>
                        <Grid item md={12} xs={12}>
                          <TextField
                            name="extendedDescription"
                            label="Long Description"
                            multiline
                            rows={5}
                            value={values.extendedDescription}
                            error={Boolean(touched.extendedDescription && errors.extendedDescription)}
                            fullWidth
                            helperText={touched.extendedDescription && errors.extendedDescription}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="extendedDescription"
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={6}>
                        <Grid item md={12} xs={12}>
                          <TextField
                            name="bankName"
                            label="Bank Name"
                            value={values.bankName}
                            error={Boolean(touched.bankName && errors.bankName)}
                            fullWidth
                            helperText={touched.bankName && errors.bankName}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="bankName"
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={6}>
                        <Grid item md={6} xs={12}>
                          <TextField
                            name="bankAddress"
                            label="Bank Address"
                            value={values.bankAddress}
                            error={Boolean(touched.bankAddress && errors.bankAddress)}
                            fullWidth
                            helperText={touched.bankAddress && errors.bankAddress}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                        <Grid item md={6} xs={12}>
                          <TextField
                            name="bankPostalCode"
                            label="Bank Postal Code"
                            value={values.bankPostalCode}
                            error={Boolean(touched.bankPostalCode && errors.bankPostalCode)}
                            fullWidth
                            helperText={touched.bankPostalCode && errors.bankPostalCode}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={6}>
                        <Grid item md={6} xs={12}>
                          <TextField
                            name="bankCity"
                            label="Bank City"
                            value={values.bankCity}
                            error={Boolean(touched.bankCity && errors.bankCity)}
                            fullWidth
                            helperText={touched.bankCity && errors.bankCity}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                        <Grid item md={6} xs={12}>
                          <Select
                            name="countryId"
                            label="Country"
                            options={JSON.parse(
                              JSON.stringify(JSON.parse(JSON.stringify(countryData).split('"countryId":').join('"key":')))
                                .split('"countryCode":')
                                .join('"value":')
                            )}
                            onChange={handleChange}
                            error={Boolean(touched.countryId && errors.countryId)}
                            helperText={touched.countryId && errors.countryId}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={6}>
                        <Grid item md={12} xs={12}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="isDefault"
                                checked={values.isDefault}
                                onChange={(e) =>
                                  handleChange({
                                    target: {
                                      name: "isDefault",
                                      value: e.target.checked,
                                    },
                                  })
                                }
                                color="primary"
                              />
                            }
                            label="Active Bank Account"
                          />
                        </Grid>
                      </Grid>
                      <Button type="submit" variant="contained" color="primary" mt={3} my={2} mr={4}>
                        Submit Bank Account
                      </Button>
                      <Button type="button" variant="contained" color="primary" mt={3} my={2} onClick={() => setOpen(false)}>
                        Close
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" component="div">
            {confirmDialog.title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {confirmDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmDialog((prev) => ({ ...prev, open: false }))} color="primary" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (confirmDialog.onConfirm) {
                confirmDialog.onConfirm();
              }
            }}
            color="primary"
            variant="contained"
            autoFocus
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddBankAccount;
